import * as React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type {
  GetInjectableActivities_ElementsFragment as InjectableActivity,
  GetInjectableActivities_ProgressFragment as InjectableActivityProgress,
} from 'bundles/ondemand-common/activity-injection/queries/__generated__/GetInjectableActivities';
import { useUpdateActivityProgress } from 'bundles/ondemand-common/activity-injection/useUpdateActivityProgress';

export const ActivityInjectionContext = createContext<ActivityInjectionContextType>({
  getInjectableActivityProgress: () => undefined,
  updateInjectableActivityProgress: () => undefined,
});

export type ActivityInjectionContextType = {
  getInjectableActivityProgress: (itemId: string) => ProgressState | undefined;
  updateInjectableActivityProgress: (itemId: string, progressState: ProgressState) => void;
};

type ProgressState = InjectableActivityProgress['progressState'];

type Props = React.PropsWithChildren<{
  injectableActivities: InjectableActivity[];
}>;

export const ActivityInjectionProvider = ({ injectableActivities, children }: Props) => {
  const itemIdToInjectableActivityId = useMemo(
    () =>
      injectableActivities.reduce((acc, activity) => {
        acc[activity.placement.itemId] = activity.id;
        return acc;
      }, {} as Record<string, string>),
    [injectableActivities]
  );

  const initialProgress = injectableActivities.reduce((acc, activity) => {
    acc[activity.placement.itemId] = activity.progress.progressState;
    return acc;
  }, {} as Record<string, ProgressState>);

  const [progress, setProgress] = useState<Record<string, ProgressState>>(initialProgress);

  const { updateActivityProgress } = useUpdateActivityProgress();

  // get the progress of an injectable activity.
  // Returns undefined if the item is not an injectable activity.
  const getInjectableActivityProgress = (itemId: string) => progress[itemId];

  // update the progress of an injectable activity.
  const updateInjectableActivityProgress = async (itemId: string, newProgressState: ProgressState) => {
    const injectableActivityId = itemIdToInjectableActivityId[itemId];

    if (!injectableActivityId) {
      return;
    }

    const initialProgressState = progress[itemId] as ProgressState;
    if (initialProgressState === newProgressState) {
      return;
    }

    // Optimistic update
    setProgress((prev) => ({ ...prev, [itemId]: newProgressState }));
    try {
      await updateActivityProgress({ injectableActivityId, progressState: newProgressState });
    } catch {
      // Rollback optimistic update on error, do not throw any errors
      setProgress((prev) => ({ ...prev, [itemId]: initialProgressState }));
    }
  };

  return (
    <ActivityInjectionContext.Provider value={{ getInjectableActivityProgress, updateInjectableActivityProgress }}>
      {children}
    </ActivityInjectionContext.Provider>
  );
};

// Allows access to the activity injection context.
// Allows default context values to be used if the context is not provided.
export const useActivityInjectionContext = () => {
  return useContext(ActivityInjectionContext);
};
